import React, { useEffect, createElement } from 'react';
const SITE_NAME = 'KittyPot';
const SITE_URL = 'https://www.kittypot.com';
/**
 * Site-wide social sharing card. Typographic rather than a product screenshot,
 * so it does not go stale when the app UI changes. Replace with a designer
 * version using the real pot mark and Poppins when one exists — 1200x630 PNG.
 */
const SOCIAL_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/eb3a47fc-a59b-4deb-be0d-d506d2b1604f.jpg';
type SEOProps = {
  /** Full document title, e.g. "KittyPot | No More Chasing Friends for Money" */
  title: string;
  /** Meta description used for search results */
  description: string;
  /** Open Graph / Twitter title. Falls back to `title`. */
  ogTitle?: string;
  /** Open Graph / Twitter description. Falls back to `description`. */
  ogDescription?: string;
  /** Path for canonical + og:url, e.g. "/faq" */
  path?: string;
  /** Optional JSON-LD structured data object */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Per-page social preview image. Falls back to the site image. */
  ogImage?: string;
  /** Keep the page out of search results (used for /404). */
  noIndex?: boolean;
};
/** Upsert a <meta> tag by name or property attribute. */
function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
/**
 * SEO/head manager. Because the project's static index.html head can't be
 * edited directly, this component keeps the document title, meta description,
 * Open Graph, Twitter card and structured data in sync with the current page.
 */
export function SEO({
  title,
  description,
  ogTitle,
  ogDescription,
  path = '/',
  jsonLd,
  ogImage,
  noIndex = false
}: SEOProps) {
  const resolvedOgTitle = ogTitle ?? title;
  const resolvedOgDescription = ogDescription ?? description;
  const image = ogImage ?? SOCIAL_IMAGE;
  const url = `${SITE_URL}${path === '/' ? '' : path}`;
  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noIndex ? 'noindex, follow' : 'index, follow');
    // Open Graph
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:title', resolvedOgTitle);
    upsertMeta('property', 'og:description', resolvedOgDescription);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:alt', 'KittyPot, split expenses not friendship');
    upsertMeta('property', 'og:locale', 'en_GB');
    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', resolvedOgTitle);
    upsertMeta('name', 'twitter:description', resolvedOgDescription);
    upsertMeta('name', 'twitter:image', image);
    upsertLink('canonical', url);
    // Structured data
    const SCRIPT_ID = 'mp-structured-data';
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = SCRIPT_ID;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, resolvedOgTitle, resolvedOgDescription, url, jsonLd, image, noIndex]);
  return null;
}